import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getVenueLocations, approveVenueLocation, rejectVenueLocation } from '../utils/api';
import { Eye, Edit2, MapPin, Clock, CheckCircle, XCircle, AlertCircle, Plus, ChevronLeft, ChevronRight, Check, X } from 'lucide-react';

const STATUS_COLORS = {
  pending: { bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-900', badge: 'bg-yellow-200 text-yellow-900 font-semibold' },
  approved: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-900', badge: 'bg-green-600 text-white font-semibold' },
  rejected: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-900', badge: 'bg-red-600 text-white font-semibold' },
  active: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-900', badge: 'bg-blue-600 text-white font-semibold' }
};

export default function VenueLocationChangeRequests() {
  const navigate = useNavigate();

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [approveConfirm, setApproveConfirm] = useState(null);
  const [rejectConfirm, setRejectConfirm] = useState(null);
  const [approveComment, setApproveComment] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);

  const limit = 10;

  const fetchRequests = async (pageNum = 1, status = '') => {
    setLoading(true);
    setError('');

    try {
      const params = new URLSearchParams({
        page: pageNum,
        limit
      });

      if (status) {
        params.append('status', status);
      }

      const response = await getVenueLocations(`?${params.toString()}`);

      setRequests(response.data || []);
      setPage(response.pagination?.page || 1);
      setTotalPages(response.pagination?.pages || 1);
    } catch (err) {
      console.error('Error fetching venue locations:', err);
      setError('Failed to load venue location requests');
      setRequests([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests(page, statusFilter);
  }, [page, statusFilter]);

  const handleStatusFilter = (status) => {
    setStatusFilter(status);
    setPage(1);
  };

  const getStatusBadgeColor = (status) => {
    return STATUS_COLORS[status] || STATUS_COLORS.pending;
  };

  const handleApprove = async () => {
    if (!approveConfirm) return;

    setApproving(true);
    try {
      await approveVenueLocation(approveConfirm._id, approveComment);
      setSuccessMessage(`✓ Venue location "${approveConfirm.venueName}" approved successfully`);
      setTimeout(() => setSuccessMessage(''), 4000);
      setApproveConfirm(null);
      setApproveComment('');
      fetchRequests(page, statusFilter);
    } catch (err) {
      console.error('Error approving venue location:', err);
      setError('Failed to approve venue location');
    } finally {
      setApproving(false);
    }
  };

  const handleReject = async () => {
    if (!rejectConfirm || !rejectReason.trim()) {
      setError('Rejection reason is required');
      return;
    }

    setRejecting(true);
    try {
      await rejectVenueLocation(rejectConfirm._id, rejectReason);
      setSuccessMessage(`✓ Venue location "${rejectConfirm.venueName}" rejected successfully`);
      setTimeout(() => setSuccessMessage(''), 4000);
      setRejectConfirm(null);
      setRejectReason('');
      fetchRequests(page, statusFilter);
    } catch (err) {
      console.error('Error rejecting venue location:', err);
      setError('Failed to reject venue location');
    } finally {
      setRejecting(false);
    }
  };

  const canEdit = (status) => {
    return status === 'pending' || status === 'rejected';
  };

  const formatActionDateTime = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHour = hours % 12 || 12;
    return `${day}-${month}-${year} at ${displayHour}:${minutes}${ampm}`;
  };

  const getActionTaken = (request) => {
    if (request.status === 'approved' && request.approvedAt) {
      return `Approved by Admin on ${formatActionDateTime(request.approvedAt)}`;
    }
    if (request.status === 'rejected' && request.rejectedAt) {
      return `Rejected by Admin on ${formatActionDateTime(request.rejectedAt)}`;
    }
    if (request.status === 'active' && request.approvedAt) {
      return `Activated by Admin on ${formatActionDateTime(request.approvedAt)}`;
    }
    return '—';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 md:p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm p-4 md:p-6 border border-gray-100">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h2 className="text-xl md:text-2xl font-semibold">Venue Location Change Requests</h2>
              <p className="text-sm text-gray-600 mt-1">Manage all your registered venue locations</p>
            </div>
            <button
              onClick={() => navigate('/register-venue-location')}
              className="flex items-center justify-center gap-2 px-4 md:px-6 py-2 md:py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium whitespace-nowrap"
            >
              <Plus size={20} />
              Add New Venue Location
            </button>
          </div>
        </div>

        {/* Status Filter */}
        <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => handleStatusFilter('')}
              className={`px-3 md:px-4 py-2 rounded-lg transition font-medium text-sm md:text-base ${
                statusFilter === ''
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
              }`}
            >
              All
            </button>
            {['pending', 'approved', 'rejected'].map(status => (
              <button
                key={status}
                onClick={() => handleStatusFilter(status)}
                className={`px-3 md:px-4 py-2 rounded-lg transition font-medium text-sm md:text-base capitalize flex items-center gap-1 ${
                  statusFilter === status
                    ? `${getStatusBadgeColor(status).badge}`
                    : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                }`}
              >
                {status === 'pending' && <Clock size={16} />}
                {status === 'approved' && <CheckCircle size={16} />}
                {status === 'rejected' && <XCircle size={16} />}
                {status}
              </button>
            ))}
          </div>
        </div>

        {/* Success Message */}
        {successMessage && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex gap-3 items-start">
              <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={20} />
              <p className="text-green-800 font-medium">{successMessage}</p>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex gap-3 items-start">
              <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
              <p className="text-red-800 font-medium">{error}</p>
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            <p className="mt-4 text-gray-600">Loading venue locations...</p>
          </div>
        )}

        {/* Empty State */}
        {!loading && requests.length === 0 && (
          <div className="bg-white rounded-xl shadow-sm p-8 md:p-12 border border-gray-100 text-center">
            <MapPin className="mx-auto mb-4 text-gray-400" size={48} />
            <h3 className="text-lg font-semibold text-gray-900">No Venue Locations Found</h3>
            <p className="text-gray-600 mt-2">
              {statusFilter
                ? `No venue locations with status "${statusFilter}" found.`
                : 'Start by registering your first venue location.'}
            </p>
            <button
              onClick={() => navigate('/register-venue-location')}
              className="mt-6 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium inline-flex items-center gap-2"
            >
              <Plus size={18} />
              Register Your First Venue
            </button>
          </div>
        )}

        {/* Requests Table */}
        {!loading && requests.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 md:px-6 py-3 text-center text-xs md:text-sm font-semibold text-gray-900 w-12">Sr.</th>
                    <th className="px-4 md:px-6 py-3 text-left text-xs md:text-sm font-semibold text-gray-900">Venue</th>
                    <th className="px-4 md:px-6 py-3 text-left text-xs md:text-sm font-semibold text-gray-900">Manager</th>
                    <th className="px-4 md:px-6 py-3 text-left text-xs md:text-sm font-semibold text-gray-900">Mobile</th>
                    <th className="px-4 md:px-6 py-3 text-left text-xs md:text-sm font-semibold text-gray-900">Status</th>
                    <th className="px-4 md:px-6 py-3 text-left text-xs md:text-sm font-semibold text-gray-900 w-48">Submitted</th>
                    <th className="px-4 md:px-6 py-3 text-left text-xs md:text-sm font-semibold text-gray-900 w-48">Action Taken</th>
                    <th className="px-4 md:px-6 py-3 text-center text-xs md:text-sm font-semibold text-gray-900">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {[...requests].sort((a, b) => new Date(b.submittedAt) - new Date(a.submittedAt)).map((request, index) => {
                    const colors = getStatusBadgeColor(request.status);
                    return (
                      <tr key={request._id} className="hover:bg-gray-50 transition">
                        <td className="px-4 md:px-6 py-4 text-center text-sm font-medium text-gray-700">{index + 1}</td>
                        <td className="px-4 md:px-6 py-4 text-sm text-gray-900 font-medium">{request.venueName}</td>
                        <td className="px-4 md:px-6 py-4 text-sm text-gray-700">{request.fullName}</td>
                        <td className="px-4 md:px-6 py-4 text-sm text-gray-700">{request.mobile}</td>
                        <td className="px-4 md:px-6 py-4 text-sm">
                          <span className={`px-3 py-1 rounded-full text-xs md:text-sm font-medium ${colors.badge}`}>
                            {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                          </span>
                        </td>
                        <td className="px-4 md:px-6 py-4 text-xs md:text-sm text-gray-600 w-48 break-words">
                          {`Submitted by ${request.fullName} on ${formatActionDateTime(request.submittedAt)}`}
                        </td>
                        <td className="px-4 md:px-6 py-4 text-xs md:text-sm text-gray-600 w-48 break-words">
                          {getActionTaken(request)}
                        </td>
                        <td className="px-4 md:px-6 py-4">
                          <div className="flex flex-wrap gap-1 justify-center">
                            <button
                              onClick={() => navigate(`/view-venue-location-change-request/${request._id}`)}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition"
                              title="View"
                            >
                              <Eye size={16} />
                            </button>

                            {canEdit(request.status) && (
                              <button
                                onClick={() => navigate(`/register-venue-location?edit=${request._id}`)}
                                className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition"
                                title="Edit"
                              >
                                <Edit2 size={16} />
                              </button>
                            )}

                            {request.status === 'pending' && (
                              <>
                                <button
                                  onClick={() => {
                                    setApproveConfirm(request);
                                    setApproveComment('');
                                  }}
                                  className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition"
                                  title="Approve"
                                >
                                  <Check size={16} />
                                </button>

                                <button
                                  onClick={() => {
                                    setRejectConfirm(request);
                                    setRejectReason('');
                                  }}
                                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                                  title="Reject"
                                >
                                  <X size={16} />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Approve Modal */}
        {approveConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-lg max-w-md w-full p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Approve Venue Location</h3>
              
              <div className="bg-gray-50 rounded-lg p-4 mb-4 space-y-3">
                <div>
                  <p className="text-xs text-gray-600 uppercase tracking-wide">Venue Name</p>
                  <p className="text-sm font-medium text-gray-900">{approveConfirm.venueName}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-600 uppercase tracking-wide">Manager</p>
                  <p className="text-sm font-medium text-gray-900">{approveConfirm.fullName}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-600 uppercase tracking-wide">Mobile</p>
                  <p className="text-sm font-medium text-gray-900">{approveConfirm.mobile}</p>
                </div>
                <div className="grid grid-cols-2 gap-3 pt-2">
                  <div>
                    <p className="text-xs text-gray-600 uppercase tracking-wide">Latitude</p>
                    <p className="text-sm font-medium text-gray-900">{approveConfirm.latitude?.toFixed(4)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600 uppercase tracking-wide">Longitude</p>
                    <p className="text-sm font-medium text-gray-900">{approveConfirm.longitude?.toFixed(4)}</p>
                  </div>
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Comments (Optional)</label>
                <textarea
                  value={approveComment}
                  onChange={(e) => setApproveComment(e.target.value)}
                  placeholder="Enter approval comments"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none text-sm"
                  rows="3"
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setApproveConfirm(null);
                    setApproveComment('');
                  }}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleApprove}
                  disabled={approving}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition font-medium disabled:bg-gray-400 flex items-center justify-center gap-2"
                >
                  {approving ? 'Approving...' : 'Approve'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Reject Modal */}
        {rejectConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-lg max-w-md w-full p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Reject Venue Location</h3>
              
              <div className="bg-gray-50 rounded-lg p-4 mb-4 space-y-3">
                <div>
                  <p className="text-xs text-gray-600 uppercase tracking-wide">Venue Name</p>
                  <p className="text-sm font-medium text-gray-900">{rejectConfirm.venueName}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-600 uppercase tracking-wide">Manager</p>
                  <p className="text-sm font-medium text-gray-900">{rejectConfirm.fullName}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-600 uppercase tracking-wide">Mobile</p>
                  <p className="text-sm font-medium text-gray-900">{rejectConfirm.mobile}</p>
                </div>
                <div className="grid grid-cols-2 gap-3 pt-2">
                  <div>
                    <p className="text-xs text-gray-600 uppercase tracking-wide">Latitude</p>
                    <p className="text-sm font-medium text-gray-900">{rejectConfirm.latitude?.toFixed(4)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600 uppercase tracking-wide">Longitude</p>
                    <p className="text-sm font-medium text-gray-900">{rejectConfirm.longitude?.toFixed(4)}</p>
                  </div>
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Rejection Reason <span className="text-red-500">*</span></label>
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Enter rejection reason (required)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none text-sm"
                  rows="3"
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setRejectConfirm(null);
                    setRejectReason('');
                  }}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleReject}
                  disabled={rejecting || !rejectReason.trim()}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition font-medium disabled:bg-gray-400 flex items-center justify-center gap-2"
                >
                  {rejecting ? 'Rejecting...' : 'Reject'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Pagination */}
        {!loading && requests.length > 0 && totalPages > 1 && (
          <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <p className="text-sm text-gray-600">
                Page {page} of {totalPages}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition disabled:opacity-50"
                >
                  <ChevronLeft size={18} />
                  <span className="hidden sm:inline">Previous</span>
                </button>
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={page === totalPages}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition disabled:opacity-50"
                >
                  <span className="hidden sm:inline">Next</span>
                  <ChevronRight size={18} />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
